import React, { useState, useEffect, useMemo, useRef } from 'react';
import { jobsAPI, clientsAPI } from '../../lib/api';
import { useBeforeUnload, ConfirmDiscardDialog, guardedContentProps, useNavigationGuard } from '../common/UnsavedGuard';
import { CalendarCheck2, AlertCircle } from 'lucide-react';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Plus, X, Briefcase } from 'lucide-react';
import RateLinesEditor from './RateLinesEditor';
import PostsEditor from './PostsEditor';
import ShiftTemplatesEditor from './ShiftTemplatesEditor';
import JobOfficersEditor from './JobOfficersEditor';
import { sitesAPI } from '../../lib/api';

const emptyPhone = { phone: '', description: '' };

const emptyJob = {
  client_id: '',
  job_number: '',
  job_description: '',
  site_address1: '', site_address2: '', site_city: '', site_state: '', site_zip: '',
  site_attention: '', site_contact_first_name: '', site_contact_last_name: '',
  site_phones: [],
  directions: '', keys_instructions: '',
  po_number: '',
  billing_frequency: '', company: '',
  sales_tax_state: '', sales_tax_county: '', payroll_tax_state: '',
  services_taxable: false, products_taxable: false,
  holiday_method: 'None',
  type: '',
  equipment: [],
  date_entered: '', date_changed: '', date_to_start: '',
  is_active: true,
  bill_to_contact_ids: [],
  // Scheduling Phase 1A (2026-09) — all optional.
  site_id: '', covered_site_ids: [],
  assigned_supervisor_employee_id: '', assigned_field_manager_user_id: '',
  assigned_regional_manager_user_id: '',
  contracted_weekly_hours: '', scheduling_enabled: false,
  dormancy_threshold_days: 30, ot_billable: true, job_status: '',
};

/** Managed dropdown with in-line "add new" support. Persists new values via
 *  jobsAPI.addLookup then bubbles the new value back so the caller can select
 *  it in the same click. */
function ManagedSelect({ value, onChange, options, kind, onAdded, placeholder, testId }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const submit = async () => {
    const v = draft.trim();
    if (!v) return;
    try {
      const { data } = await jobsAPI.addLookup(kind, v);
      onAdded && onAdded(data);
      onChange(data.value);
      setDraft('');
      setAdding(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add value');
    }
  };
  const opts = options.map(o => (typeof o === 'string' ? { value: o, id: o } : o));
  return (
    <div className="flex items-center gap-2">
      <Select value={value || '__none__'} onValueChange={(v) => onChange(v === '__none__' ? '' : v)}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder || 'Select…'} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— None —</SelectItem>
          {opts.map(o => <SelectItem key={o.id || o.value} value={o.value}>{o.value}</SelectItem>)}
        </SelectContent>
      </Select>
      {kind && (
        adding ? (
          <div className="flex items-center gap-1">
            <Input
              value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              placeholder="New value" className="h-9 w-32"
              data-testid={`${testId}-add-input`} autoFocus
            />
            <Button size="sm" variant="ghost" onClick={submit} data-testid={`${testId}-add-save`}>Save</Button>
            <Button size="icon" variant="ghost" onClick={() => { setAdding(false); setDraft(''); }}><X className="w-4 h-4" /></Button>
          </div>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => setAdding(true)} data-testid={`${testId}-add-btn`}>
            <Plus className="w-4 h-4" />
          </Button>
        )
      )}
    </div>
  );
}

export default function JobEditor({ open, onOpenChange, jobId, clientId, canEdit, onSaved }) {
  const isCreate = !jobId;
  const [form, setForm] = useState({ ...emptyJob, client_id: clientId || '' });
  const [meta, setMeta] = useState(null);
  const [client, setClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savedJobId, setSavedJobId] = useState(jobId || null);
  const [savedJobNumber, setSavedJobNumber] = useState('');
  const [tab, setTab] = useState('details');
  const [sites, setSites] = useState([]);
  // Unsaved-changes guard (C1): baseline snapshot of the form as last loaded or
  // saved; `dirty` is any divergence from it.
  const [baseline, setBaseline] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dirty = useMemo(
    () => !!baseline && JSON.stringify(form) !== baseline,
    [form, baseline]);
  useBeforeUnload(open && dirty);
  // Scheduling readiness (C4): why this job is / is not on Schedules yet.
  const [readiness, setReadiness] = useState(null);
  const readinessReqId = useRef(0);  // drop out-of-order readiness responses
  // v4.38.0: non-blocking company Security license advisory for the job's state.
  const [licenseWarning, setLicenseWarning] = useState(null);
  const licenseReqId = useRef(0);
  // Bill rates are Finance, Payroll and Admin, plus anyone given the grant
  // (2026-09-24 decision). Managers hold `jobs:view` and reach this editor, so
  // the tab has to resolve from the capability rather than from the screen.
  const perms = useEffectivePermissions();
  const canSeeBillRates = perms.can('bill_rates', 'view') || perms.hasGrant('payroll');


  // Sites belong to the client, so the picker follows whichever client is selected.
  useEffect(() => {
    if (!form.client_id) { setSites([]); return; }
    sitesAPI.list({ client_id: form.client_id, is_active: true })
      .then(({ data }) => setSites(data))
      .catch(() => setSites([]));
  }, [form.client_id]);

  useEffect(() => {
    setSavedJobId(jobId || null);
    if (open) {
      // Fresh open: land on Details and start clean so a leftover form from a
      // previous session never trips the unsaved prompt before load finishes.
      setTab('details');
      setBaseline('');
      setReadiness(null);          // never show the previous job's readiness
      setLicenseWarning(null);
      loadReadiness(jobId);        // fetch by the prop id, not stale savedJobId
      loadLicenseWarning(jobId);
      loadInitial();
    }
  }, [open, jobId, clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadInitial = async () => {
    setLoading(true);
    try {
      const [metaRes, clientsRes] = await Promise.all([
        jobsAPI.meta(),
        clientsAPI.list({ is_active: true }),
      ]);
      setMeta(metaRes.data);
      setClients(clientsRes.data);
      if (jobId) {
        const { data } = await jobsAPI.get(jobId);
        const nextForm = {
          ...emptyJob,
          ...data,
          site_phones: data.site_phones || [],
          equipment: data.equipment || [],
          bill_to_contact_ids: data.bill_to_contact_ids || [],
        };
        setForm(nextForm);
        setBaseline(JSON.stringify(nextForm));
        setSavedJobNumber(data.job_number || '');
        if (data.client_id) {
          const cRes = await clientsAPI.get(data.client_id);
          setClient(cRes.data);
        }
      } else {
        const nextForm = { ...emptyJob, client_id: clientId || '', is_active: true };
        setForm(nextForm);
        setBaseline(JSON.stringify(nextForm));
        setSavedJobNumber('');
        if (clientId) {
          const cRes = await clientsAPI.get(clientId);
          setClient(cRes.data);
        } else {
          setClient(null);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const changePhone = (idx, k, v) => setForm(f => {
    const phones = [...(f.site_phones || [])];
    phones[idx] = { ...phones[idx], [k]: v };
    return { ...f, site_phones: phones };
  });
  const addPhone = () => {
    if ((form.site_phones || []).length >= 3) return;
    setForm(f => ({ ...f, site_phones: [...(f.site_phones || []), { ...emptyPhone }] }));
  };
  const removePhone = (idx) => setForm(f => ({
    ...f, site_phones: (f.site_phones || []).filter((_, i) => i !== idx),
  }));

  const toggleEquipment = (tag) => setForm(f => {
    const has = (f.equipment || []).includes(tag);
    return { ...f, equipment: has ? f.equipment.filter(t => t !== tag) : [...(f.equipment || []), tag] };
  });

  const toggleBillTo = (contactId) => setForm(f => {
    const has = (f.bill_to_contact_ids || []).includes(contactId);
    return {
      ...f,
      bill_to_contact_ids: has
        ? f.bill_to_contact_ids.filter(id => id !== contactId)
        : [...(f.bill_to_contact_ids || []), contactId],
    };
  });

  // Client selection updates the invoice-recipient picker below.
  useEffect(() => {
    if (!form.client_id) { setClient(null); return; }
    // Only re-fetch if the currently loaded client doesn't match.
    if (client?.id === form.client_id) return;
    (async () => {
      try {
        const { data } = await clientsAPI.get(form.client_id);
        setClient(data);
      } catch { /* ignore */ }
    })();
  }, [form.client_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form.client_id) { toast.error('Select a client'); return false; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Server ignores blank job_number on create → mints. Empty string is fine.
        job_number: (form.job_number || '').trim() || null,
        date_entered: form.date_entered || null,
        date_changed: form.date_changed || null,
        date_to_start: form.date_to_start || null,
        contracted_weekly_hours: form.contracted_weekly_hours === '' ? null
          : Number(form.contracted_weekly_hours),
        dormancy_threshold_days: form.dormancy_threshold_days === '' ? null
          : Number(form.dormancy_threshold_days),
        site_id: form.site_id || null,
        job_status: form.job_status || null,
      };
      let savedId = savedJobId;
      if (savedJobId) {
        const { data } = await jobsAPI.update(savedJobId, payload);
        toast.success(`Job ${data.job_number} saved`);
        setSavedJobNumber(data.job_number);
      } else {
        const { data } = await jobsAPI.create(payload);
        toast.success(`Job ${data.job_number} created`);
        savedId = data.id;
        setSavedJobId(data.id);
        setSavedJobNumber(data.job_number);
        setTab('rate-lines');  // Jump to rate lines after create.
      }
      // The form is now the saved truth — clear the dirty flag.
      setBaseline(JSON.stringify(form));
      loadReadiness(savedId);
      onSaved && onSaved();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // C1: nothing closes this editor out from under unsaved work.
  const requestClose = () => {
    if (dirty) setConfirmOpen(true);
    else onOpenChange(false);
  };
  const discardAndClose = () => {
    setBaseline(JSON.stringify(form));  // stop the beforeunload warning
    setConfirmOpen(false);
    onOpenChange(false);
  };
  const saveAndClose = async () => {
    const ok = await save();
    if (ok) { setConfirmOpen(false); onOpenChange(false); }
  };

  // App-wide guard: menu clicks, in-app links and browser Back/Forward also prompt.
  useNavigationGuard('job-editor', {
    getDirty: () => open && dirty,
    save,
    discard: discardAndClose,
  });

  const loadReadiness = (id) => {
    if (!id) { setReadiness(null); return; }
    const reqId = ++readinessReqId.current;
    jobsAPI.schedulingReadiness(id)
      .then(({ data }) => { if (reqId === readinessReqId.current) setReadiness(data); })
      .catch(() => { if (reqId === readinessReqId.current) setReadiness(null); });
  };

  const loadLicenseWarning = (id) => {
    if (!id) { setLicenseWarning(null); return; }
    const reqId = ++licenseReqId.current;
    jobsAPI.securityLicenseCheck(id)
      .then(({ data }) => { if (reqId === licenseReqId.current) setLicenseWarning(data?.warning || null); })
      .catch(() => { if (reqId === licenseReqId.current) setLicenseWarning(null); });
  };

  const staticEquip = meta?.static_equipment || [];
  const otherEquip = (meta?.lookups?.equipment_other || []).map(x => x.value);
  const companies = meta?.companies || [];
  const holidayMethods = meta?.holiday_methods || ['None', 'STD', 'PFL'];
  const jobTypes = meta?.lookups?.type || [];
  const billingFrequencies = meta?.lookups?.billing_frequency || [];
  const clientContacts = client?.additional_contacts || [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) requestClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="job-editor-dialog"
        {...guardedContentProps(requestClose)}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Briefcase className="w-5 h-5 text-slate-500" />
            {isCreate ? 'New Job' : `Job ${savedJobNumber || form.job_number || ''}`}
            {!form.is_active && <Badge variant="secondary">Inactive</Badge>}
          </DialogTitle>
          <DialogDescription>
            {isCreate
              ? 'Jobs hang off a client and drive billing. Save details first, then add rate lines.'
              : `Client: ${client?.name || '—'}`}
          </DialogDescription>
        </DialogHeader>

        {licenseWarning && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="job-license-warning">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{licenseWarning}</span>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="min-w-0 w-full">
            <TabsList className="max-w-full overflow-x-auto justify-start">
              <TabsTrigger value="details" data-testid="job-tab-details">Details</TabsTrigger>
              {canSeeBillRates && (
                <TabsTrigger value="rate-lines" disabled={!savedJobId} data-testid="job-tab-rate-lines">
                  Rate lines {savedJobId ? '' : '(save first)'}
                </TabsTrigger>
              )}
              <TabsTrigger value="posts" disabled={!savedJobId} data-testid="job-tab-posts">Posts</TabsTrigger>
              <TabsTrigger value="officers" disabled={!savedJobId} data-testid="job-tab-officers">Officers</TabsTrigger>
              <TabsTrigger value="shifts" disabled={!savedJobId} data-testid="job-tab-shifts">Shifts</TabsTrigger>
              <TabsTrigger value="scheduling" data-testid="job-tab-scheduling">Scheduling</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-6 mt-4">
              {/* Header row: client + job number + status */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Client *</Label>
                  <Select value={form.client_id} onValueChange={(v) => change('client_id', v)} disabled={!canEdit}>
                    <SelectTrigger data-testid="job-client-select"><SelectValue placeholder="Select client…" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name} ({c.customer_number})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Job Number</Label>
                  <Input
                    value={form.job_number || ''}
                    onChange={(e) => change('job_number', e.target.value)}
                    placeholder="(auto YYYY-#####)"
                    disabled={!canEdit || !isCreate}
                    data-testid="job-number-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    {isCreate
                      ? 'Leave blank to auto-generate. Legacy numbers can be typed verbatim.'
                      : 'Job numbers are permanent and never reused.'}
                  </p>
                </div>
                <div className="flex items-end gap-3">
                  <div>
                    <Label>Status</Label>
                    <div className="flex items-center gap-2 h-9">
                      <Switch
                        checked={form.is_active}
                        onCheckedChange={(v) => change('is_active', v)}
                        disabled={!canEdit}
                        data-testid="job-active-toggle"
                      />
                      <span className="text-sm">{form.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label>Job Description</Label>
                <Textarea
                  value={form.job_description || ''}
                  onChange={(e) => change('job_description', e.target.value)}
                  disabled={!canEdit}
                  rows={2}
                  data-testid="job-description"
                />
              </div>

              {/* Site block */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-1.5 mb-3">Site</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-2"><Label>Address 1</Label>
                    <Input value={form.site_address1 || ''} onChange={(e) => change('site_address1', e.target.value)} disabled={!canEdit} data-testid="job-site-addr1" />
                  </div>
                  <div className="md:col-span-2"><Label>Address 2</Label>
                    <Input value={form.site_address2 || ''} onChange={(e) => change('site_address2', e.target.value)} disabled={!canEdit} />
                  </div>
                  <div><Label>City</Label>
                    <Input value={form.site_city || ''} onChange={(e) => change('site_city', e.target.value)} disabled={!canEdit} data-testid="job-site-city" />
                  </div>
                  <div><Label>State</Label>
                    <Input value={form.site_state || ''} onChange={(e) => change('site_state', e.target.value)} disabled={!canEdit} data-testid="job-site-state" />
                  </div>
                  <div><Label>ZIP</Label>
                    <Input value={form.site_zip || ''} onChange={(e) => change('site_zip', e.target.value)} disabled={!canEdit} />
                  </div>
                  <div><Label>Attention</Label>
                    <Input value={form.site_attention || ''} onChange={(e) => change('site_attention', e.target.value)} disabled={!canEdit} />
                  </div>
                  <div><Label>Contact First</Label>
                    <Input value={form.site_contact_first_name || ''} onChange={(e) => change('site_contact_first_name', e.target.value)} disabled={!canEdit} />
                  </div>
                  <div><Label>Contact Last</Label>
                    <Input value={form.site_contact_last_name || ''} onChange={(e) => change('site_contact_last_name', e.target.value)} disabled={!canEdit} />
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Site phones (up to 3)</Label>
                    {canEdit && (form.site_phones || []).length < 3 && (
                      <Button size="sm" variant="outline" onClick={addPhone} data-testid="job-add-phone">
                        <Plus className="w-4 h-4 mr-1.5" /> Add phone
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {(form.site_phones || []).map((p, i) => (
                      <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-center">
                        <Input value={p.phone || ''} onChange={(e) => changePhone(i, 'phone', e.target.value)}
                          placeholder="Phone" disabled={!canEdit} data-testid={`job-phone-${i}`} />
                        <Input value={p.description || ''} onChange={(e) => changePhone(i, 'description', e.target.value)}
                          placeholder="Description (WinTeam names live here)" disabled={!canEdit} data-testid={`job-phone-desc-${i}`} />
                        {canEdit && (
                          <Button size="icon" variant="ghost" onClick={() => removePhone(i)}><X className="w-4 h-4" /></Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div><Label>Directions</Label>
                    <Textarea value={form.directions || ''} onChange={(e) => change('directions', e.target.value)} disabled={!canEdit} rows={2} />
                  </div>
                  <div><Label>Keys / Instructions</Label>
                    <Textarea value={form.keys_instructions || ''} onChange={(e) => change('keys_instructions', e.target.value)} disabled={!canEdit} rows={2} />
                  </div>
                </div>
              </div>

              {/* Billing block */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-1.5 mb-3">Billing</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label>PO Number</Label>
                    <Input value={form.po_number || ''} onChange={(e) => change('po_number', e.target.value)} disabled={!canEdit} data-testid="job-po-number" />
                  </div>
                  <div>
                    <Label>Billing Frequency</Label>
                    <ManagedSelect
                      value={form.billing_frequency}
                      onChange={(v) => change('billing_frequency', v)}
                      options={billingFrequencies}
                      kind={canEdit ? 'billing_frequency' : null}
                      onAdded={() => loadInitial()}
                      testId="job-billing-frequency"
                    />
                  </div>
                  <div>
                    <Label>Company</Label>
                    <Select value={form.company || '__none__'} onValueChange={(v) => change('company', v === '__none__' ? '' : v)} disabled={!canEdit}>
                      <SelectTrigger data-testid="job-company"><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {companies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Sales Tax State</Label>
                    <Input value={form.sales_tax_state || ''} onChange={(e) => change('sales_tax_state', e.target.value)} disabled={!canEdit} />
                  </div>
                  <div><Label>Sales Tax County</Label>
                    <Input value={form.sales_tax_county || ''} onChange={(e) => change('sales_tax_county', e.target.value)} disabled={!canEdit} />
                  </div>
                  <div><Label>Payroll Tax State</Label>
                    <Input value={form.payroll_tax_state || ''} onChange={(e) => change('payroll_tax_state', e.target.value)} disabled={!canEdit} />
                  </div>
                  <div className="flex items-center gap-2 h-9 mt-6">
                    <Switch checked={form.services_taxable} onCheckedChange={(v) => change('services_taxable', v)} disabled={!canEdit} data-testid="job-services-taxable" />
                    <Label className="text-sm">Services Taxable</Label>
                  </div>
                  <div className="flex items-center gap-2 h-9 mt-6">
                    <Switch checked={form.products_taxable} onCheckedChange={(v) => change('products_taxable', v)} disabled={!canEdit} data-testid="job-products-taxable" />
                    <Label className="text-sm">Products Taxable</Label>
                  </div>
                  <div>
                    <Label>Holiday Method</Label>
                    <Select value={form.holiday_method || 'None'} onValueChange={(v) => change('holiday_method', v)} disabled={!canEdit}>
                      <SelectTrigger data-testid="job-holiday-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {holidayMethods.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Type</Label>
                    <ManagedSelect
                      value={form.type}
                      onChange={(v) => change('type', v)}
                      options={jobTypes}
                      kind={canEdit ? 'type' : null}
                      onAdded={() => loadInitial()}
                      testId="job-type"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label>Bill To / Invoice Recipients (from client&apos;s contacts)</Label>
                  {clientContacts.length === 0 ? (
                    <p className="text-xs text-slate-400 mt-1">
                      {form.client_id ? 'This client has no additional contacts yet.' : 'Select a client to see recipients.'}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2" data-testid="job-bill-to-picker">
                      {clientContacts.map(ct => {
                        const on = (form.bill_to_contact_ids || []).includes(ct.id);
                        const name = [ct.first_name, ct.last_name].filter(Boolean).join(' ') || '(unnamed)';
                        return (
                          <label
                            key={ct.id}
                            className={`flex items-start gap-2 p-2 border rounded-md cursor-pointer transition ${on ? 'bg-emerald-50 border-emerald-300' : 'border-slate-200 hover:bg-slate-50'}`}
                            data-testid={`job-bill-to-${ct.id}`}
                          >
                            <Switch checked={on} onCheckedChange={() => toggleBillTo(ct.id)} disabled={!canEdit} />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{name}</p>
                              {ct.email && <p className="text-xs text-slate-500">{ct.email}</p>}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Equipment */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-1.5 mb-3">Equipment</h4>
                <div className="flex flex-wrap gap-2">
                  {staticEquip.map(tag => {
                    const on = (form.equipment || []).includes(tag);
                    return (
                      <Badge
                        key={tag}
                        variant={on ? 'default' : 'outline'}
                        className={`cursor-pointer ${on ? '' : 'hover:bg-slate-100'}`}
                        onClick={() => canEdit && toggleEquipment(tag)}
                        data-testid={`job-equip-${tag.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {tag}
                      </Badge>
                    );
                  })}
                  {otherEquip.map(tag => {
                    const on = (form.equipment || []).includes(tag);
                    return (
                      <Badge
                        key={tag}
                        variant={on ? 'default' : 'outline'}
                        className={`cursor-pointer ${on ? '' : 'hover:bg-slate-100'}`}
                        onClick={() => canEdit && toggleEquipment(tag)}
                      >
                        {tag}
                      </Badge>
                    );
                  })}
                </div>
                <div className="mt-2">
                  <ManagedSelect
                    value=""
                    onChange={(v) => v && !(form.equipment || []).includes(v) && change('equipment', [...(form.equipment || []), v])}
                    options={otherEquip}
                    kind={canEdit ? 'equipment_other' : null}
                    onAdded={() => loadInitial()}
                    placeholder="Add other equipment…"
                    testId="job-equip-other"
                  />
                </div>
              </div>

              {/* Dates */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-1.5 mb-3">Dates</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label>Date Entered</Label>
                    <Input type="date" value={form.date_entered || ''} onChange={(e) => change('date_entered', e.target.value)} disabled={!canEdit} />
                  </div>
                  <div><Label>Date Changed</Label>
                    <Input type="date" value={form.date_changed || ''} onChange={(e) => change('date_changed', e.target.value)} disabled={!canEdit} />
                  </div>
                  <div><Label>Date To Start</Label>
                    <Input type="date" value={form.date_to_start || ''} onChange={(e) => change('date_to_start', e.target.value)} disabled={!canEdit} data-testid="job-date-to-start" />
                  </div>
                </div>
              </div>

              {canEdit && (
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={requestClose} data-testid="job-details-close">Close</Button>
                  <Button onClick={save} disabled={saving} data-testid="job-save-btn">
                    {saving ? 'Saving…' : (savedJobId ? 'Save' : 'Create Job')}
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="rate-lines" className="mt-4">
              {savedJobId && canSeeBillRates ? (
                <RateLinesEditor jobId={savedJobId} canEdit={canEdit} meta={meta} />
              ) : (
                <p className="text-sm text-slate-500">Save the job first, then add rate lines.</p>
              )}
            </TabsContent>

            <TabsContent value="posts" className="mt-4">
              {savedJobId && <PostsEditor jobId={savedJobId} canEdit={canEdit} meta={meta} />}
            </TabsContent>

            <TabsContent value="officers" className="mt-4">
              {savedJobId && <JobOfficersEditor jobId={savedJobId} canEdit={canEdit} />}
            </TabsContent>

            <TabsContent value="shifts" className="mt-4">
              {savedJobId && <ShiftTemplatesEditor jobId={savedJobId} canEdit={canEdit} />}
            </TabsContent>

            <TabsContent value="scheduling" className="mt-4 space-y-5">
              <p className="text-sm text-slate-500 max-w-2xl">
                All optional. A job stays out of scheduling until it is switched on here or
                approved through a work order, which is what keeps the existing job list from
                being flooded during rollout.
              </p>

              {savedJobId && readiness && (
                <div data-testid="scheduling-readiness"
                  className={`rounded-lg border p-4 ${readiness.on_schedules
                    ? 'border-emerald-200 bg-emerald-50/60' : 'border-amber-200 bg-amber-50/60'}`}>
                  {readiness.on_schedules ? (
                    <div className="flex items-start gap-2">
                      <CalendarCheck2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-emerald-900" data-testid="readiness-on">
                          This job is on Schedules.
                        </p>
                        {!readiness.can_build_week && readiness.reasons.length > 0 && (
                          <p className="text-xs text-emerald-800 mt-1">
                            Before a week can be built: {readiness.reasons.join(' ')}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-amber-900" data-testid="readiness-off">
                          This job is not on Schedules yet. Here is what is needed:
                        </p>
                        <ul className="list-disc list-inside text-xs text-amber-800 mt-1 space-y-0.5">
                          {readiness.reasons.map((r, i) => (
                            <li key={i} data-testid={`readiness-reason-${i}`}>{r}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Site</Label>
                  <Select value={form.site_id || '__none__'}
                    onValueChange={(v) => change('site_id', v === '__none__' ? '' : v)}>
                    <SelectTrigger data-testid="job-site">
                      <SelectValue placeholder="Uses the address on this job" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="__none__">— None (use the address on this job) —</SelectItem>
                      {sites.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}{s.site_code ? ` · ${s.site_code}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    When set, the site is the source of truth for the address. The address on
                    this job is kept and stays the fallback.
                  </p>
                </div>
                <div>
                  <Label>Job status</Label>
                  <Select value={form.job_status || '__none__'}
                    onValueChange={(v) => change('job_status', v === '__none__' ? '' : v)}>
                    <SelectTrigger data-testid="job-status"><SelectValue placeholder="Not set" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Not set —</SelectItem>
                      {['pending_setup', 'active', 'on_hold', 'dormant', 'terminated'].map(s => (
                        <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Refines Active/Inactive — terminated archives the job.
                  </p>
                </div>
                <div className="md:col-span-2">
                  <Label>Covered sites (roving route)</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {sites.map(s => {
                      const on = (form.covered_site_ids || []).includes(s.id);
                      return (
                        <Button key={s.id} type="button" size="sm"
                          variant={on ? 'default' : 'outline'}
                          data-testid={`job-covered-${s.id}`}
                          onClick={() => change('covered_site_ids', on
                            ? form.covered_site_ids.filter(x => x !== s.id)
                            : [...(form.covered_site_ids || []), s.id])}>
                          {s.name}
                        </Button>
                      );
                    })}
                    {!sites.length && (
                      <span className="text-xs text-slate-500">No sites on this client yet.</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Reference only — never scheduled or billed against, and it appears on no
                    invoice. A roving job with no fixed address is perfectly valid.
                  </p>
                </div>
                <div>
                  <Label>Contracted weekly hours</Label>
                  <Input type="number" step="0.25" value={form.contracted_weekly_hours ?? ''}
                    onChange={(e) => change('contracted_weekly_hours', e.target.value)}
                    placeholder="Budget for variance reporting" data-testid="job-weekly-hours" />
                </div>
                <div>
                  <Label>Dormancy threshold (days)</Label>
                  <Input type="number" value={form.dormancy_threshold_days ?? ''}
                    onChange={(e) => change('dormancy_threshold_days', e.target.value)}
                    data-testid="job-dormancy" />
                  <p className="text-xs text-slate-500 mt-1">Raise it for seasonal or on-call work.</p>
                </div>
                <div>
                  <Label>Assigned supervisor (employee id)</Label>
                  <Input value={form.assigned_supervisor_employee_id || ''}
                    onChange={(e) => change('assigned_supervisor_employee_id', e.target.value)}
                    data-testid="job-supervisor" />
                </div>
                <div>
                  <Label>Field manager (user id)</Label>
                  <Input value={form.assigned_field_manager_user_id || ''}
                    onChange={(e) => change('assigned_field_manager_user_id', e.target.value)}
                    data-testid="job-field-manager" />
                </div>
                <div>
                  <Label>Regional manager (user id)</Label>
                  <Input value={form.assigned_regional_manager_user_id || ''}
                    onChange={(e) => change('assigned_regional_manager_user_id', e.target.value)}
                    data-testid="job-regional-manager" />
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch checked={!!form.scheduling_enabled}
                      onCheckedChange={(v) => change('scheduling_enabled', v)}
                      data-testid="job-scheduling-enabled" />
                    <Label>Scheduling enabled</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={form.ot_billable !== false}
                      onCheckedChange={(v) => change('ot_billable', v)}
                      data-testid="job-ot-billable" />
                    <Label>Overtime billable</Label>
                  </div>
                </div>
              </div>
              {canEdit && (
                <div className="flex items-center justify-between gap-2 pt-4 border-t">
                  <p className="text-xs text-slate-500">
                    Changes here (including “Scheduling enabled”) are saved when you press Save.
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={requestClose} data-testid="job-scheduling-close">Close</Button>
                    <Button onClick={save} disabled={saving} data-testid="job-scheduling-save-btn">
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
      <ConfirmDiscardDialog
        open={confirmOpen}
        saving={saving}
        onSave={saveAndClose}
        onDiscard={discardAndClose}
        onCancel={() => setConfirmOpen(false)}
      />
    </Dialog>
  );
}
